import PageLayout from "@/components/feature/PageLayout";
import ScrollReveal from "@/components/base/ScrollReveal";
import CurrentGroupOfferings from "@/components/feature/CurrentGroupOfferings";

import groupTherapyHeroImage from "../../media/Group Therapy hero.png";
import groupTherapySupportingImage from "../../media/Group therapy supporting image.png";

const groupTherapyDetails = [
  {
    label: "Service type",
    value: "Current play-based child group psychotherapy cohorts",
  },
  {
    label: "Format",
    value: "Six weekly 60-minute sessions, in person in Larchmont",
  },
  {
    label: "Focus",
    value: "Emotional regulation, peer relationships, social skills, and play",
  },
  {
    label: "Participation",
    value: "Fall 2026 registration is open for children ages 6–7",
  },
];

const issues = [
  "Feeling isolated or alone in what you are experiencing",
  "Difficulty expressing emotions, needs, or boundaries",
  "Wanting support from others with shared experiences",
  "Learning new coping skills in a guided setting",
  "Navigating stress, anxiety, or major life transitions",
  "Building confidence in communication and relationships",
  "Practicing emotional awareness and self-reflection",
  "Receiving structured support in a community-based format",
];

const whatToExpect = [
  {
    num: "01",
    title: "A guided therapeutic space",
    body:
      "Group therapy is facilitated by a therapist who helps guide discussion, maintain structure, and create a supportive environment for reflection and growth.",
    color: "#B4A6E3",
  },
  {
    num: "02",
    title: "Connection with others",
    body:
      "Groups help clients recognize that they are not alone. Hearing from others can reduce shame, increase perspective, and support a deeper sense of connection.",
    color: "#7D54FC",
  },
  {
    num: "03",
    title: "Skill-building in real time",
    body:
      "Group therapy gives clients a place to practice communication, emotional regulation, boundary-setting, and coping strategies in a safe and structured setting.",
    color: "#3C6FF8",
  },
  {
    num: "04",
    title: "A fit-based process",
    body:
      "Because group therapy depends on readiness, goals, and group dynamics, participation may include an intake or consultation to confirm clinical fit.",
    color: "#6B742F",
  },
];

export default function GroupTherapyPage() {
  return (
    <>

      <PageLayout>
        {/* Hero */}
        <section className="relative flex min-h-[520px] w-full items-center overflow-hidden md:min-h-[620px]">
          <div className="absolute inset-0 z-0">
            <img
              src={groupTherapyHeroImage}
              alt="Group therapy at Perspective Therapy"
              className="h-full w-full object-cover object-center"
            />

            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(120deg, rgba(28,20,12,0.58) 0%, rgba(42,30,18,0.42) 50%, rgba(60,40,20,0.18) 100%)",
              }}
            />

            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(to top, rgba(20,14,8,0.42) 0%, transparent 50%)",
              }}
            />
          </div>

          <div className="relative z-10 w-full px-6 pb-20 pt-36 md:px-12 lg:px-20">
            <div className="max-w-3xl">
              <p className="mb-4 font-manrope text-[13px] font-semibold uppercase tracking-widest text-white/90">
                Group Therapy
              </p>

              <h1 className="mb-6 font-fraunces text-4xl font-light leading-[1.15] text-white md:text-5xl lg:text-[3.4rem]">
                Group therapy for{" "}
                <em
                  className="not-italic font-normal"
                  style={{ color: "#C9BCEE" }}
                >
                  support, connection, and growth
                </em>
              </h1>

              <div className="max-w-xl rounded-2xl bg-white/80 p-3 backdrop-blur-sm md:p-4">
                <p className="font-manrope text-lg leading-relaxed text-[#2B2B2B]/90">
                  General information about group therapy, plus two current
                  play-based child groups open for Fall 2026 registration.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Details */}
        <section className="w-full py-14 md:py-16">
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {groupTherapyDetails.map((item) => (
                <ScrollReveal key={item.label} variant="fadeUp" delay={0}>
                  <div
                    className="h-full rounded-2xl border bg-white/80 p-6"
                    style={{ borderColor: "rgba(203,191,175,0.35)" }}
                  >
                    <p className="font-manrope text-[12px] font-semibold uppercase tracking-[0.18em] text-[#8B7355]">
                      {item.label}
                    </p>

                    <p className="mt-3 font-manrope text-[14px] leading-relaxed text-[#2B2B2B]/75">
                      {item.value}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Why Group Therapy */}
        <section className="w-full py-20 md:py-28">
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">
              <ScrollReveal variant="fadeLeft" delay={0}>
                <div className="h-[280px] w-full overflow-hidden rounded-2xl sm:h-[340px] md:h-[500px]">
                  <img
                    src={groupTherapySupportingImage}
                    alt="A supportive group therapy discussion"
                    className="h-full w-full object-cover object-center"
                  />
                </div>
              </ScrollReveal>

              <div>
                <ScrollReveal variant="fadeIn" delay={0}>
                  <div className="mb-4 flex items-center gap-2">
                    <div
                      className="h-[2px] w-5 rounded-full"
                      style={{
                        background: "linear-gradient(90deg, #B4A6E3, #7D54FC)",
                      }}
                    />
                    <span
                      className="font-manrope text-[13px] font-semibold uppercase tracking-widest"
                      style={{ color: "#7D54FC" }}
                    >
                      Why Group Therapy
                    </span>
                  </div>
                </ScrollReveal>

                <ScrollReveal variant="fadeUp" delay={80}>
                  <h2 className="mb-6 font-fraunces text-3xl font-light leading-snug text-[#2B2B2B] md:text-4xl">
                    Group therapy creates space for{" "}
                    <em className="not-italic" style={{ color: "#7D54FC" }}>
                      shared understanding and real-time support
                    </em>
                  </h2>
                </ScrollReveal>

                <ScrollReveal variant="fadeUp" delay={160}>
                  <p className="mb-8 font-manrope text-[15px] leading-relaxed text-[#2B2B2B]/82">
                    While individual therapy gives clients focused one-on-one
                    support, group therapy offers the opportunity to learn and
                    grow alongside others. In a group, clients can hear different
                    perspectives, practice communication, strengthen emotional
                    awareness, and experience the relief of being understood.
                  </p>
                </ScrollReveal>

                <ScrollReveal variant="fadeUp" delay={240}>
                  <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {issues.map((issue) => (
                      <div key={issue} className="flex items-center gap-2.5">
                        <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                          <i
                            className="ri-check-line text-sm"
                            style={{ color: "#7D54FC" }}
                          />
                        </span>
                        <span className="font-manrope text-[14px] text-[#2B2B2B]/80">
                          {issue}
                        </span>
                      </div>
                    ))}
                  </div>
                </ScrollReveal>

                <ScrollReveal variant="fadeUp" delay={320}>
                  <div className="flex flex-col gap-4 sm:flex-row">
                    <a
                      href="/contact"
                      className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-7 py-4 font-manrope text-[15px] font-semibold text-white transition-colors"
                      style={{ background: "#7D54FC" }}
                      onMouseEnter={(e) =>
                        ((e.currentTarget as HTMLAnchorElement).style.background =
                          "#6a40e8")
                      }
                      onMouseLeave={(e) =>
                        ((e.currentTarget as HTMLAnchorElement).style.background =
                          "#7D54FC")
                      }
                    >
                      Ask About Group Therapy{" "}
                      <i className="ri-arrow-right-line" />
                    </a>

                    <a
                      href="#group-types"
                      className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap font-manrope text-[15px] font-medium text-[#2B2B2B]/78 transition-colors hover:text-[#2B2B2B]"
                    >
                      View group types <i className="ri-arrow-right-line" />
                    </a>
                  </div>
                </ScrollReveal>
              </div>
            </div>
          </div>
        </section>

        {/* What to Expect */}
        <section
          className="w-full py-20 md:py-28"
          style={{
            background:
              "linear-gradient(180deg, #F8F5EE 0%, #F5F1E8 45%, #F2EDE4 100%)",
          }}
        >
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="mb-14 max-w-3xl">
              <ScrollReveal variant="fadeIn" delay={0}>
                <span
                  className="mb-4 block font-manrope text-[13px] font-semibold uppercase tracking-widest"
                  style={{ color: "#6B742F" }}
                >
                  What to Expect
                </span>
              </ScrollReveal>

              <ScrollReveal variant="fadeUp" delay={80}>
                <h2 className="mb-5 font-fraunces text-3xl font-light leading-snug text-[#2B2B2B] md:text-4xl">
                  A thoughtful and supportive group process
                </h2>
              </ScrollReveal>

              <ScrollReveal variant="fadeUp" delay={160}>
                <p className="font-manrope text-[15px] leading-relaxed text-[#2B2B2B]/78">
                  Group therapy depends on fit, readiness, and the goals of the
                  group. The first step is usually to ask about current group
                  options and confirm whether a group is appropriate for the
                  client’s needs.
                </p>
              </ScrollReveal>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:gap-8">
              {whatToExpect.map((item, i) => (
                <ScrollReveal key={item.num} variant="fadeUp" delay={i * 90}>
                  <div
                    className="rounded-2xl border bg-white/80 p-7"
                    style={{ borderColor: item.color + "22" }}
                  >
                    <div
                      className="mb-5 h-[3px] w-10 rounded-full"
                      style={{ background: item.color }}
                    />

                    <div className="flex items-start gap-4">
                      <span
                        className="mt-0.5 shrink-0 font-manrope text-sm font-bold"
                        style={{ color: item.color }}
                      >
                        {item.num}
                      </span>

                      <div>
                        <h3 className="mb-2 font-fraunces text-lg font-medium text-[#2B2B2B]">
                          {item.title}
                        </h3>

                        <p className="font-manrope text-[15px] leading-relaxed text-[#2B2B2B]/78">
                          {item.body}
                        </p>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        <CurrentGroupOfferings heading="Current groups at Perspective Therapy" />

        {/* Final CTA */}
        <section className="w-full py-16 md:py-20">
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="mx-auto max-w-3xl text-center">
              <ScrollReveal variant="fadeUp" delay={0}>
                <h2 className="mb-5 font-fraunces text-3xl font-light leading-snug text-[#2B2B2B] md:text-4xl">
                  Ready to ask about current group therapy options?
                </h2>

                <p className="mx-auto mb-6 max-w-lg font-manrope text-[15px] leading-relaxed text-[#2B2B2B]/78">
                  Group availability may change based on schedule, clinical fit,
                  and client needs. Reach out to ask which group option may be
                  appropriate.
                </p>
              </ScrollReveal>

              <ScrollReveal variant="fadeUp" delay={80}>
                <div className="flex flex-col justify-center gap-4 sm:flex-row">
                  <a
                    href="/contact"
                    className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full px-7 py-4 font-manrope text-[15px] font-semibold text-white transition-colors"
                    style={{ background: "#3C6FF8" }}
                    onMouseEnter={(e) =>
                      ((e.currentTarget as HTMLAnchorElement).style.background =
                        "#2a5de6")
                    }
                    onMouseLeave={(e) =>
                      ((e.currentTarget as HTMLAnchorElement).style.background =
                        "#3C6FF8")
                    }
                  >
                    Ask About Group Therapy{" "}
                    <i className="ri-arrow-right-line" />
                  </a>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>
      </PageLayout>
    </>
  );
}
